<template>
  <div
    class="table_r w-full justify-start items-center text-[var(--text)] gap-2 p-4 bg-[var(--fundoForm)] rounded-[10px] bg-opacity-[0.55] flex flex-col overflow-x-auto"
    :style="{
      height: '100%',
      paddingTop: styled.noPaddingTop ? '1em' : '2rem',
    }"
    v-if="show && visibility"
  >
    <div class="title_table">
      <div class="title_settings">
        <div
          class="table_title capitalize items-center relative"
          style="display: flex; gap: 1em"
        >
          {{ table_title }}
        </div>

        <!-- Search input -->
        <input
          type="search"
          id="table-search"
          v-model="searchQuery"
          @input="handleSearch"
          placeholder="buscar..."
          ref="searchInput"
        />

        <div class="close_btn_div relative">
          <!-- Export dropdown button -->
          <button
            v-if="show_export"
            class="bg-[--button] text-white pl-2 pr-2 rounded-md flex items-center"
            @click="toggleDropdown"
          >
            Exportar
            <i v-if="!isDropdownOpen" class="fa-solid fa-caret-down ml-2"></i>
            <i v-else class="fa-solid fa-caret-up ml-2"></i>
          </button>

          <!-- Dropdown menu -->
          <div
            v-if="isDropdownOpen"
            class="w-[6.9rem] text-white absolute top-[2rem] left-0 mt-2 bg-[#9797EF] shadow-lg rounded-md z-10"
          >
            <ul>
              <li
                class="flex items-center justify-between px-4 py-2 hover:bg-[#ff9b05] hover:rounded-md cursor-pointer"
                @click="exportData('pdf')"
              >
                PDF
                <i class="fa-solid fa-file-pdf"></i>
              </li>
              <li
                class="flex items-center justify-between px-4 py-2 hover:bg-[#ff9b05] hover:rounded-md cursor-pointer"
                @click="exportData('csv')"
              >
                CSV
                <i class="fa-solid fa-file-csv"></i>
              </li>
               <li
                class="flex items-center justify-between px-4 py-2 hover:bg-[#ff9b05] hover:rounded-md cursor-pointer"
                @click="exportData('xlsx')"
              >
                XLSX
                <i class="fa-solid fa-file-excel"></i>
              </li>
            </ul>
          </div>

          <!-- Close button -->
          <button
            v-if="show_close_btn"
            class="bg-[--button] text-white ml-2 pl-2 pr-2 rounded-md"
            @click="$emit('close')"
          >
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Advanced Filters (Collapsible) -->
    <div v-if="show_filters" class="w-full mb-4">
      <button
        @click="showAdvancedFilters = !showAdvancedFilters"
        class="bg-[--button] text-white px-3 py-1 rounded-md mb-2"
      >
        Filtros Avançados
        <i :class="showAdvancedFilters ? 'fa-caret-up' : 'fa-caret-down'" class="fas ml-2"></i>
      </button>
      
      <div v-if="showAdvancedFilters" class="bg-[var(--fundoForm)] p-4 rounded-md">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="filter in availableFilters" :key="filter.key" class="flex flex-col">
            <label class="text-sm mb-1">{{ filter.label }}</label>
            <select
              v-if="filter.type === 'select'"
              v-model="activeFilters[filter.key]"
              @change="handleFilterChange(filter.key)"
              class="bg-[var(--fundoForm)] border border-gray-600 rounded px-2 py-1 text-[var(--text)]"
            >
              <option value="">Todos</option>
              <option v-for="option in filter.options" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
            <input
              v-else
              v-model="activeFilters[filter.key]"
              @input="handleFilterChange(filter.key)"
              :type="filter.type || 'text'"
              class="bg-[var(--fundoForm)] border border-gray-600 rounded px-2 py-1 text-[var(--text)]"
              :placeholder="filter.placeholder"
            />
          </div>
        </div>
        <button
          @click="clearFilters"
          class="mt-3 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md"
        >
          Limpar Filtros
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
    </div>

    <!-- Data table -->
    <div v-else class="w-full overflow-x-auto">
      <table class="w-full text-[var(--text)] rounded-lg overflow-hidden" style="background-color: transparent !important;">
        <thead class="bg-[#1f2937]">
          <tr>
            <!-- Bulk selection checkbox header -->
            <th v-if="enableBulkSelection" class="px-4 py-3 text-left w-12">
              <input
                type="checkbox"
                :key="`sel-all-${isAllSelected}-${isIndeterminate}`"
                :checked="isAllSelected"
                :indeterminate="isIndeterminate"
                @change="toggleSelectAll"
                class="form-checkbox h-4 w-4 text-blue-600"
              />
            </th>
            <th
              v-for="column in columns"
              :key="column.key"
              class="px-4 py-3 text-left transition-colors"
              :class="column.sortable !== false ? 'cursor-pointer' : ''"
              @click="column.sortable !== false ? handleSort(column.key) : null"
            >
              <div class="flex items-center justify-between">
                <span>{{ column.label }}</span>
                <!-- Only show sort indicators if column is sortable -->
                <div v-if="column.sortable !== false" class="flex flex-col ml-2">
                  <i
                    class="fas fa-caret-up text-xs"
                    :class="{
                      'text-blue-400': sortField === column.key && sortOrder === 'asc',
                      'text-gray-400': !(sortField === column.key && sortOrder === 'asc')
                    }"
                  ></i>
                  <i
                    class="fas fa-caret-down text-xs -mt-1"
                    :class="{
                      'text-blue-400': sortField === column.key && sortOrder === 'desc',
                      'text-gray-400': !(sortField === column.key && sortOrder === 'desc')
                    }"
                  ></i>
                </div>
              </div>
            </th>
            <th v-if="show_actions" class="px-4 py-3 text-center">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in tableData"
            :key="item.id || index"
            :class="[
              'border-b border-gray-600 transition-colors cursor-pointer',
              selectedRowId && selectedRowId === item.id 
                ? 'bg-blue-600 bg-opacity-50' 
                : 'hover:bg-gray-700 hover:bg-opacity-50'
            ]"
            @click="handleRowClick(item)"
          >
            <!-- Bulk selection checkbox -->
            <td v-if="enableBulkSelection" class="px-4 py-3">
              <input
                type="checkbox"
                :key="`sel-${item.id}-${isItemSelected(item)}`"
                :checked="isItemSelected(item)"
                :disabled="!isItemSelected(item) && !isSelectableFn(item)"
                :title="!isItemSelected(item) && !isSelectableFn(item) ? 'Não é possível selecionar em conjunto com a seleção atual' : ''"
                @change="toggleItemSelection(item)"
                @click.stop
                class="form-checkbox h-4 w-4 text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
              />
            </td>
            <td
              v-for="column in columns"
              :key="column.key"
              class="px-4 py-3"
            >
              <!-- Custom slot for column content -->
              <slot :name="`column-${column.key}`" :item="item" :value="getNestedValue(item, column)">
                <!-- Enhanced column rendering with nested object support -->
                <span 
                  v-if="column.type === 'badge'"
                  :class="getBadgeClass(getNestedValue(item, column), column)"
                  class="px-2 py-1 rounded-full text-xs font-medium"
                >
                  {{ formatColumnValue(getNestedValue(item, column), column.type) }}
                </span>
                <span v-else>
                  {{ formatColumnValue(getNestedValue(item, column), column.type) }}
                </span>
              </slot>
            </td>
            <td v-if="show_actions" class="px-4 py-3 text-center">
              <slot name="actions" :item="item">
                <button
                  @click.stop="$emit('edit', item)"
                  class="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded mr-2"
                >
                  <i class="fas fa-edit"></i>
                </button>
                <button
                  @click.stop="$emit('delete', item)"
                  class="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty state -->
      <div v-if="!loading && tableData.length === 0" class="text-center py-8 text-gray-400">
        Nenhum registro encontrado
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="!loading && pagination" class="flex justify-between items-center w-full mt-4">
      <div class="text-sm text-gray-400">
        Mostrando {{ pagination.from }} a {{ pagination.to }} de {{ pagination.total }} registros
      </div>
      
      <div class="flex items-center space-x-2">
        <!-- Page size selector -->
        <select
          v-model="pageSize"
          @change="handlePageSizeChange"
          class="bg-[var(--fundoForm)] border border-gray-600 rounded px-2 py-1 text-[var(--text)] text-sm"
        >
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
        
        <!-- Pagination buttons -->
        <button
          @click="goToPage(pagination.current_page - 1)"
          :disabled="(pagination.has_previous !== undefined ? !pagination.has_previous : pagination.current_page <= 1)"
          class="bg-[--button] text-white px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        
        <span class="text-[var(--text)] px-2">
          Página {{ pagination.current_page }} de {{ pagination.total_pages }}
        </span>
        
        <button
          @click="goToPage(pagination.current_page + 1)"
          :disabled="(pagination.has_next !== undefined ? !pagination.has_next : pagination.current_page >= pagination.total_pages)"
          class="bg-[--button] text-white px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'BackofficeDataTable',
  props: {
    // Display props
    table_title: {
      type: String,
      default: 'Tabela de Dados'
    },
    show: {
      type: Boolean,
      default: true
    },
    visibility: {
      type: Boolean,
      default: true
    },
    styled: {
      type: Object,
      default: () => ({})
    },
    
    // Data props
    apiUrl: {
      type: String,
      required: true
    },
    columns: {
      type: Array,
      required: true
      // Format: [{ key: 'name', label: 'Nome', type: 'text' }]
    },
    
    // Feature toggles
    show_export: {
      type: Boolean,
      default: true
    },
    show_close_btn: {
      type: Boolean,
      default: false
    },
    show_actions: {
      type: Boolean,
      default: true
    },
    show_filters: {
      type: Boolean,
      default: true
    },
    
    // Filters
    availableFilters: {
      type: Array,
      default: () => []
      // Format: [{ key: 'status', label: 'Status', type: 'select', options: [...] }]
    },
    
    // Row selection
    selectedRowId: {
      type: [Number, String],
      default: null
    },
    
    // Bulk selection
    enableBulkSelection: {
      type: Boolean,
      default: false
    },
    selectedItems: {
      type: Array,
      default: () => []
    },
    // Optional guard: (item) => boolean. Rows failing it get a disabled
    // checkbox instead of a select-then-get-rejected round trip — avoids any
    // "reject after the fact" flow whose visual state depends on re-render
    // timing. Defaults to allowing everything.
    isSelectableFn: {
      type: Function,
      default: () => true
    },

    // Initial parameters
    initialParams: {
      type: Object,
      default: () => ({})
    }
  },
  
  data() {
    return {
      // Search and filters
      searchQuery: '',
      searchTimeout: null,
      activeFilters: {},
      showAdvancedFilters: true,
      
      // Sorting
      sortField: '',
      sortOrder: 'asc',
      
      // Pagination
      currentPage: 1,
      pageSize: 25,
      
      // Data
      tableData: [],
      pagination: null,
      loading: false,
      filteredIds: [], // Store all filtered IDs from backend
      
      // Export
      isDropdownOpen: false,
      
      // Filter timeout for debouncing
      filterTimeout: null
    }
  },
  
  mounted() {
    this.loadData()
  },
  
  watch: {
    availableFilters: {
      handler(newFilters) {
        // Log when filters change for debugging
        console.log('BackofficeDataTable: availableFilters changed', newFilters)
        console.log('BackofficeDataTable: availableFilters length:', newFilters.length)
        newFilters.forEach(filter => {
          console.log(`BackofficeDataTable: Filter ${filter.key} has ${filter.options?.length || 0} options`)
        })
      },
      deep: true,
      immediate: true
    },
    
    // Watch for changes in initialParams and reload data
    initialParams: {
      handler() {
        this.currentPage = 1
        this.loadData()
      },
      deep: true
      // No immediate: true — mounted() handles the initial load
    }
  },
  
  computed: {
    // Bulk selection computed properties
    isAllSelected() {
      if (!this.enableBulkSelection || this.tableData.length === 0) return false
      const selectable = this.tableData.filter(item => this.isItemSelected(item) || this.isSelectableFn(item))
      if (selectable.length === 0) return false
      return selectable.every(item => this.isItemSelected(item))
    },

    isIndeterminate() {
      if (!this.enableBulkSelection || this.tableData.length === 0) return false
      const selectable = this.tableData.filter(item => this.isItemSelected(item) || this.isSelectableFn(item))
      const selectedCount = selectable.filter(item => this.isItemSelected(item)).length
      return selectedCount > 0 && selectedCount < selectable.length
    }
  },
  
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = this.buildRequestParams()
        const response = await axios.get(this.apiUrl, { params })
        
        if (response.data) {
          this.tableData = response.data.results || response.data.data || []
          this.pagination = this.extractPagination(response.data)
          // Store filtered IDs if available (from ItemPrecarioDataTable backend)
          this.filteredIds = response.data.filtered_ids || []
        }
      } catch (error) {
        console.error('Error loading data:', error)
        this.tableData = []
      } finally {
        this.loading = false
      }
    },
    
    buildRequestParams() {
      const params = {
        page: this.currentPage,
        page_size: this.pageSize,
        search: this.searchQuery,
        ...this.initialParams
      }
      
      // Add sorting
      if (this.sortField) {
        params.order_by = this.sortOrder === 'desc' ? `-${this.sortField}` : this.sortField
      }
      
      // Add active filters
      Object.entries(this.activeFilters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params[`filter_${key}`] = value
        }
      })
      
      return params
    },
    
    extractPagination(data) {
      // Handle custom pagination format from GenericPaginationView
      if (data.total_count !== undefined) {
        const currentPage = data.current_page || this.currentPage
        const pageSize = data.page_size || this.pageSize
        const totalCount = data.total_count
        
        // Update currentPage if it came from server
        if (data.current_page && data.current_page !== this.currentPage) {
          this.currentPage = data.current_page
        }
        
        return {
          total: totalCount,
          current_page: currentPage,
          total_pages: data.total_pages || Math.ceil(totalCount / pageSize),
          from: totalCount > 0 ? ((currentPage - 1) * pageSize) + 1 : 0,
          to: totalCount > 0 ? Math.min(currentPage * pageSize, totalCount) : 0,
          has_next: !!data.links?.next,
          has_previous: !!data.links?.previous
        }
      }
      // Fallback for standard Django REST framework pagination
      else if (data.count !== undefined) {
        const totalCount = data.count
        const currentPage = this.currentPage
        const pageSize = this.pageSize
        
        return {
          total: totalCount,
          current_page: currentPage,
          total_pages: Math.ceil(totalCount / pageSize),
          from: totalCount > 0 ? ((currentPage - 1) * pageSize) + 1 : 0,
          to: totalCount > 0 ? Math.min(currentPage * pageSize, totalCount) : 0,
          has_next: currentPage < Math.ceil(totalCount / pageSize),
          has_previous: currentPage > 1
        }
      }
      return null
    },
    
    handleSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.loadData()
      }, 500)
    },
    
     handleSort(field) {
      // Find the column configuration
      const column = this.columns.find(col => col.key === field)
      
      // Only sort if column is sortable (default is true)
      if (column && column.sortable === false) {
        return
      }
      
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortOrder = 'asc'
      }
      this.currentPage = 1
      this.loadData()
    },
    
    handleFilterChange(filterKey) {
      // First emit the filter change to parent for cascading updates
      this.$emit('filters-changed', this.activeFilters, filterKey)
      
      // For input fields, debounce the API call
      const filter = this.availableFilters.find(f => f.key === filterKey);
      if (filter && filter.type !== 'select') {
        // Debounce for input fields
        clearTimeout(this.filterTimeout);
        this.filterTimeout = setTimeout(() => {
          this.applyFilters()
        }, 500);
      } else {
        // Immediate for select dropdowns
        this.applyFilters()
      }
    },
    
    applyFilters() {
      this.currentPage = 1
      this.loadData()
    },
    
    clearFilters() {
      this.activeFilters = {}
      this.currentPage = 1
      this.$emit('filters-changed', this.activeFilters, 'clear')
      this.loadData()
    },
    
    // Method to clear specific filters (called from parent)
    clearSpecificFilters(filterKeys) {
      filterKeys.forEach(key => {
        // Vue 3: no $delete helper needed, plain property deletion is reactive.
        delete this.activeFilters[key]
      })
    },
    
    goToPage(page) {
      if (page >= 1 && page <= (this.pagination?.total_pages || 1)) {
        this.currentPage = page
        this.loadData()
      }
    },
    
    handlePageSizeChange() {
      this.currentPage = 1
      this.loadData()
    },
    
      // Replace your existing formatColumnValue method with this enhanced version
    formatColumnValue(value, type) {
      if (value === null || value === undefined || value === '') return ''
      
      switch (type) {
        case 'date':
          try {
            return new Date(value).toLocaleDateString('pt-PT')
          } catch {
            return String(value)
          }
          
        case 'datetime':
          try {
            return new Date(value).toLocaleString('pt-PT')
          } catch {
            return String(value)
          }
          
        case 'currency':
          try {
            return new Intl.NumberFormat('pt-PT', {
              style: 'currency',
              currency: 'EUR'
            }).format(parseFloat(value))
          } catch {
            return String(value)
          }
          
        case 'number':
          try {
            return new Intl.NumberFormat('pt-PT').format(parseFloat(value))
          } catch {
            return String(value)
          }
          
        case 'percentage':
          try {
            return new Intl.NumberFormat('pt-PT', {
              style: 'percent',
              minimumFractionDigits: 1,
              maximumFractionDigits: 2
            }).format(parseFloat(value) / 100)
          } catch {
            return String(value)
          }
          
        case 'boolean':
          return value ? 'Sim' : 'Não'
          
        case 'badge':
          return String(value)
          
        case 'truncate':
          const str = String(value)
          return str.length > 50 ? str.substring(0, 50) + '...' : str
          
        default:
          return String(value)
      }
    },
    
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },
    
    exportData(format) {
      this.$emit('export', { format, data: this.tableData })
      this.isDropdownOpen = false
    },
    
    // Bulk selection methods
    isItemSelected(item) {
      return this.selectedItems.some(selectedItem => selectedItem.id === item.id)
    },
    
    toggleItemSelection(item) {
      const isSelected = this.isItemSelected(item)
      let newSelectedItems

      if (isSelected) {
        newSelectedItems = this.selectedItems.filter(selectedItem => selectedItem.id !== item.id)
      } else {
        // Should already be blocked by the disabled checkbox, this is just a
        // defensive backstop.
        if (!this.isSelectableFn(item)) return
        newSelectedItems = [...this.selectedItems, item]
      }

      this.$emit('selection-changed', newSelectedItems)
    },
    
    toggleSelectAll() {
      // selectedItems is owned by the parent and may already hold items from
      // other pages (or other filter passes, for the filteredIds case below)
      // — every branch here must merge/subtract against it, never replace it
      // wholesale, or paging silently drops previously selected rows.
      if (this.isAllSelected) {
        // Deselect exactly what "select all" would have added on this page
        // (or, in filteredIds mode, everything matching the current filter),
        // leaving any selection made elsewhere untouched.
        const idsToRemove = new Set(
          this.filteredIds && this.filteredIds.length > 0
            ? this.filteredIds
            : this.tableData.map(item => item.id)
        )
        this.$emit('selection-changed', this.selectedItems.filter(item => !idsToRemove.has(item.id)))
      } else if (this.filteredIds && this.filteredIds.length > 0) {
        // Select every item matching the current filter, across all pages —
        // merged into whatever was already selected. _isFilteredOnly stand-ins
        // (rows we don't have full data for) can't be checked against
        // isSelectableFn, so they're let through as before.
        const currentPageIds = this.tableData.map(item => item.id)
        const otherFilteredIds = this.filteredIds.filter(id => !currentPageIds.includes(id))
        const otherItems = otherFilteredIds.map(id => ({ id, _isFilteredOnly: true }))
        const existingIds = new Set(this.selectedItems.map(item => item.id))
        const toAdd = [...this.tableData, ...otherItems].filter(
          item => !existingIds.has(item.id) && (item._isFilteredOnly || this.isSelectableFn(item))
        )
        this.$emit('selection-changed', [...this.selectedItems, ...toAdd])
      } else {
        // No filteredIds: just add this page's items to the existing selection.
        const existingIds = new Set(this.selectedItems.map(item => item.id))
        const toAdd = this.tableData.filter(item => !existingIds.has(item.id) && this.isSelectableFn(item))
        this.$emit('selection-changed', [...this.selectedItems, ...toAdd])
      }
    },
    
    handleRowClick(item) {
      if (!this.enableBulkSelection) {
        this.$emit('row-click', item)
      }
      // If bulk selection is enabled, we let the checkbox handle selection
    },
    
    refresh() {
      this.loadData()
    },
   
    getNestedValue(item, column) {
      // If column has a custom computed function, use it
      if (column.computed && typeof column.computed === 'function') {
        try {
          return column.computed(item)
        } catch (error) {
          console.warn(`Error in computed function for column ${column.key}:`, error)
          return null
        }
      }
      
      // Handle simple keys (no dots)
      if (!column.key.includes('.')) {
        return item[column.key]
      }
      
      // Handle nested keys (with dots)
      return this.getValueByPath(item, column.key)
    },
    
   
    getValueByPath(obj, path) {
      if (!obj || !path) return null
      
      const keys = path.split('.')
      let value = obj
      
      for (const key of keys) {
        // Handle array access like 'items[0]'
        if (key.includes('[') && key.includes(']')) {
          const arrayKey = key.substring(0, key.indexOf('['))
          const index = parseInt(key.substring(key.indexOf('[') + 1, key.indexOf(']')))
          
          if (value[arrayKey] && Array.isArray(value[arrayKey]) && value[arrayKey][index] !== undefined) {
            value = value[arrayKey][index]
          } else {
            return null
          }
        } else {
          // Regular object property access
          if (value && typeof value === 'object' && value.hasOwnProperty(key)) {
            value = value[key]
          } else {
            return null
          }
        }
      }
      
      return value
    },
    
    /**
     * Get badge CSS classes based on value and column configuration
     */
    getBadgeClass(value, column) {
      const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium'
      
      if (!column.badgeColors) {
        // Default badge colors
        const defaultColors = {
          active: 'bg-green-500 text-white',
          ativo: 'bg-green-500 text-white',
          inactive: 'bg-red-500 text-white',
          inativo: 'bg-red-500 text-white',
          pending: 'bg-yellow-500 text-black',
          pendente: 'bg-yellow-500 text-black',
          planeamento: 'bg-yellow-500 text-black',
          completed: 'bg-blue-500 text-white',
          concluido: 'bg-blue-500 text-white',
          cancelled: 'bg-gray-500 text-[var(--text)]',
          cancelado: 'bg-gray-500 text-[var(--text)]'
        }
        
        return `${baseClasses} ${defaultColors[String(value).toLowerCase()] || 'bg-gray-400 text-[var(--text)]'}`
      }
      
      // Custom badge colors from column configuration
      const colorKey = String(value).toLowerCase()
      const colorClass = column.badgeColors[colorKey] || column.badgeColors[value] || 'secondary'
      
      const colorMap = {
        success: 'bg-green-500 text-white',
        danger: 'bg-red-500 text-white',
        warning: 'bg-yellow-500 text-black',
        info: 'bg-blue-500 text-white',
        secondary: 'bg-gray-500 text-[var(--text)]',
        primary: 'bg-indigo-500 text-white'
      }
      
      return `${baseClasses} ${colorMap[colorClass] || 'bg-gray-400 text-[var(--text)]'}`
    },
  }
}
</script>

<style scoped>
.title_table {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 1rem;
}

.title_settings {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.table_title {
  font-size: 1.2rem;
  font-weight: bold;
  color: white;
}

#table-search {
  background-color: var(--fundoForm);
  border: 1px solid #6b7280;
  border-radius: 0.375rem;
  padding: 0.5rem;
  color: white;
  flex: 1;
  max-width: 300px;
}

#table-search:focus {
  outline: none;
  border-color: #3b82f6;
}

.close_btn_div {
  display: flex;
  align-items: center;
}

/* Table specific styles */
table {
  background-color: var(--fundoForm);
}

thead th {
  background-color: #1f2937;
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 0.75rem;
  font-weight: 600;
}

tbody td {
  font-size: 0.75rem;
}

.glass-app.theme-light thead,
.glass-app.theme-light thead th {
  background-color: #f1f3f8;
  color: #1f2937;
}

.glass-app.theme-light .table_title {
  color: #1f2937;
}

.glass-app.theme-light #table-search {
  color: #1f2937;
  border-color: rgba(15, 23, 42, 0.15);
}

tbody tr:hover {
  background-color: rgba(var(--fundoForm-rgb), 0.75);
}
</style>